/**
 * marketingGuides.service.ts
 *
 * Marketing > Marketing Guides — the PDF guides corporate has made available to
 * this location. Read-only here; corporate manages them in the corp dashboard.
 *
 * The list never carries the file URL. `openMarketingGuide` asks the API for it,
 * which also records the view or download corporate sees in its counts.
 *
 * The files are public S3 objects, so a plain `<a download>` would be ignored
 * (cross-origin) and just open the PDF. Download therefore fetches the file as a
 * blob and saves it under its original name; that needs the bucket's CORS to
 * allow GET from this origin, and falls back to opening the PDF in a tab when it
 * does not.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

/** Lowered `modules.moduleTitle` — must match MarketingGuideService::MODULE_KEY. */
export const MARKETING_GUIDES_MODULE_KEY = 'marketing guides';

export interface MarketingGuide {
  id: number;
  title: string;
  description: string | null;
  categoryId: number | null;
  categoryName: string | null;
  thumbnailUrl: string | null;
  originalFilename: string;
  fileSize: number;
  updateDate: string | null;
}

export type MarketingGuideEvent = 'view' | 'download';

export function getMarketingGuides(): Promise<MarketingGuide[]> {
  return apiClient
    .get<ApiResponse<MarketingGuide[]>>('/marketing-guides')
    .then((response) => response.data.data);
}

export function openMarketingGuide(
  id: number,
  type: MarketingGuideEvent,
): Promise<{ url: string; filename: string }> {
  return apiClient
    .post<ApiResponse<{ url: string; filename: string }>>(`/marketing-guides/${id}/open`, { type })
    .then((response) => response.data.data);
}

/**
 * Open via a synthesized anchor click rather than `window.open`, which popup
 * blockers reject when it runs after an await — same as openKbAttachment.
 */
function openInNewTab(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Record a view, then open the PDF in a new tab. */
export async function viewMarketingGuide(id: number): Promise<void> {
  const { url } = await openMarketingGuide(id, 'view');
  openInNewTab(url);
}

/**
 * Record a download, then save the PDF under its original file name.
 *
 * Resolves to 'saved' when the file was downloaded, or 'opened' when the fetch
 * failed (typically bucket CORS) and the PDF was opened in a tab instead.
 */
export async function downloadMarketingGuide(id: number): Promise<'saved' | 'opened'> {
  const { url, filename } = await openMarketingGuide(id, 'download');

  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'guide.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Give the browser a tick to start the save before the URL is released.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return 'saved';
  } catch {
    openInNewTab(url);
    return 'opened';
  }
}
